import { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, Microphone, Sparkle, Users, Briefcase, ArrowUUpLeft, Waveform, LockKey, PencilSimple, CalendarStar, NotePencil, ArrowSquareOut, CaretDown, CaretUp, Wrench, Check, Rows, SquaresFour, Kanban } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import NoteViewerDialog from "@/components/NoteViewerDialog";
import AttendeeSelector from "@/components/AttendeeSelector";
import PartnerContactsMultiAutocomplete from "@/components/PartnerContactsMultiAutocomplete";
import MeetingRecallSearch from "@/components/MeetingRecallSearch";
import MarkdownDocument from "@/components/MarkdownDocument";
import MemoActions from "@/components/MemoActions";
import { toMemoMarkdown } from "@/lib/briefBody";
import AttachmentBadge from "@/components/AttachmentBadge";
import BriefingReviewDialog from "@/components/BriefingReviewDialog";
import BriefingAudioStrip from "@/components/BriefingAudioStrip";
import OrganizedNoteBody from "@/components/OrganizedNoteBody";
import { formatApiError } from "@/lib/apiError";
import ActivityPill from "@/components/ActivityPill";
import MeetingNoteCard from "@/components/MeetingNoteCard";
import { MARKDOWN_NOTE_TYPES, previewText } from "@/lib/noteTypes";
import { CONTINUOUS_DYNAMICS } from "@/components/method/continuousDynamics";
import { NOTE_TYPE_LABEL } from "@/lib/enumLabels";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// Map ?dynamic=… query hint (from the /method 'Capture in Briefing Studio →'
// jump-off) to the canonical dynamic key + tag written into the briefing.
// A tag is used (not the note_type Literal) so the advisor stays free to pick
// meeting / client / engagement / follow-up as the underlying briefing shape.
const DYNAMIC_QUERY_TO_KEY = { connections: "connections", leadership: "leadership" };
const DYNAMIC_TAG = { connections: "connections-lens", leadership: "leadership-lens" };

const CORE_TYPES = ["meeting", "insight", "strategic_memo", "call_log", "diagnostic_memo"];
// Iter 85.34 · Activity Briefing joins the family of note-types that flow
// through the Briefing Studio composer (they carry a briefing_target and
// participate in the Organize / Promote-to-Task pipeline just like every
// other Meeting Note). The distinguishing signals live in
// activity_date / activity_location / activity_service_category on the
// Note itself — no parallel domain object, no bespoke pipeline.
const BRIEFING_TYPES = new Set(["client_briefing", "engagement_briefing", "activity_briefing"]);
const ALL_TYPES = [...CORE_TYPES, "client_briefing", "engagement_briefing", "activity_briefing"];
// Iter 85.34 · Yadid Advisory service classes an activity can carry.
// Mirrors Engagement.engagement_type so a delegation, dinner, convening,
// or facilitation session can be classified using the same taxonomy the
// firm already uses at the engagement level.
const ACTIVITY_SERVICE_CATEGORIES = [
  { value: "trusted_advisor", label: "Trusted Advisor / Strategic Advisory" },
  { value: "strategic_initiative", label: "Strategic Initiative" },
  { value: "executive_convening", label: "Executive Convening" },
  { value: "executive_communication", label: "Executive Communication & Facilitation" },
];

const STATE_PILL = {
  draft: "border-muted-foreground/60 text-muted-foreground",
  in_review: "border-brass/60 text-brass",
  applied: "border-emerald-500/60 text-emerald-500",
  archived: "border-border text-muted-foreground opacity-60",
};

const STATE_LABEL = {
  draft: "Draft",
  in_review: "In Review",
  applied: "Applied",
  archived: "Archived",
};

/**
 * Briefing Studio — the single workspace where advisors capture unstructured
 * material and turn it into structured YadidOS records. Note-taking, meeting
 * recording, client briefings, and engagement briefings all live here so an
 * arc from raw audio → advisor-reviewed proposals → canonical records stays
 * in one place.
 */
export default function BriefingStudio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [engs, setEngs] = useState([]);
  const [open, setOpen] = useState(false);
  // Banner state — surfaces "You're capturing under the X lens" when the
  // advisor arrived here via a Continuous Dynamic jump-off. Cleared when the
  // dialog closes so the banner doesn't persist across unrelated activity.
  const [dynamicIntent, setDynamicIntent] = useState(null);
  // Inherited-context state — surfaces "Inherited from engagement" pill when
  // the advisor launched Briefing Studio from a specific engagement (via
  // `?client=…&engagement=…`). Fields are locked by default but an
  // intentional "Change context" action unlocks them. Kept per-dialog so
  // opening a fresh briefing doesn't carry stale inheritance.
  const [inheritedContext, setInheritedContext] = useState(null); // { clientId, engagementId } | null
  const [contextLocked, setContextLocked] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("open");
  const [reviewNote, setReviewNote] = useState(null);
  // Iter 80 · Separate state for the canonical Note Viewer modal. Once a
  // record has been AI-reviewed, the partner should be able to reopen it
  // as a regular working Note (interactive Action Items etc.) — that's
  // this dialog. The Review-with-YadidAI button keeps opening the
  // BriefingReviewDialog for the extraction workflow.
  const [viewerNote, setViewerNote] = useState(null);
  // Iter 85.45 · Session-scoped set of note ids whose Tools drawer is
  // expanded on the Advisory Desk. Default is collapsed for every card
  // so the feed reads cleanly; toggling persists across ordinary
  // re-renders (feed refresh, filter changes) within the same session.
  // Not persisted to localStorage — a fresh page load returns every
  // card to the compact default state.
  const [expandedTools, setExpandedTools] = useState(() => new Set());
  // Iter 85.85 · Card density preference. `comfortable` (default) shows
  // the full organized-note preview + raw body inline on every card;
  // `compact` collapses each card to its header + toolbar row so
  // partners can scan more engagements at a glance. Persisted in
  // localStorage so the preference survives reloads.
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem("yadid.desk.density") === "compact" ? "compact" : "comfortable"; }
    catch { return "comfortable"; }
  });
  useEffect(() => {
    try { localStorage.setItem("yadid.desk.density", density); } catch { /* noop */ }
  }, [density]);
  const isDeskCompact = density === "compact";

  // Iter 85.98 · View mode. List = today's vertical row layout;
  // Grid = 3-column header-only tiles for high-volume weeks;
  // Kanban = 4 columns grouped by note-type (Meetings / Activity
  // briefings / Strategic memos / Follow-ups) for quick "find the
  // last strategic memo for X" scans. Persisted per-user; List
  // remains the safe default so nothing breaks for advisors who
  // don't touch the toggle.
  const [deskView, setDeskView] = useState(() => {
    try {
      const v = localStorage.getItem("yadid.desk.view");
      return v === "grid" || v === "kanban" ? v : "list";
    } catch { return "list"; }
  });
  useEffect(() => {
    try { localStorage.setItem("yadid.desk.view", deskView); } catch { /* noop */ }
  }, [deskView]);
  const toggleTools = (id) => setExpandedTools((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [form, setForm] = useState({
    title: "",
    body: "",
    note_type: "meeting",
    client_id: "",
    engagement_id: "",
    tags: "",
    attendees: [],  // Iter 85.65 · array of display names — AttendeeSelector-managed. Legacy notes may still carry the legacy comma-separated string; the composer treats [] as the fresh-form baseline.
    briefing_kind: "meeting", // meeting | client_briefing | engagement_briefing | activity_briefing
    new_client_name: "",
    new_engagement_name: "",
    is_new_client: false,
    is_new_engagement: false,
    // Iter 85.34 · Activity Briefing metadata — sent up on POST /notes
    // when note_type == "activity_briefing" and blank-ignored otherwise.
    activity_date: "",
    activity_location: "",
    activity_service_category: "",
    // Iter 85.67 · Contextual "Partner" — an individual or external
    // collaborator relevant to THIS specific note/memo (e.g. Rabbi Chaim
    // Steinmetz on a Moral Hearts Alliance engagement). Distinct from
    // the primary Partner Organization referenced by `client_id`. Free
    // text so the advisor can type anyone even if they aren't a Contact
    // yet; if they later add the person as a Contact, historical notes
    // continue to read cleanly because the display name never changes.
    partner: "",
    // Iter 85.68 · Optional pointer to the canonical Contact selected
    // from the Partner autocomplete. null while free-text.
    partner_contact_id: null,
    // Iter 85.76 · Multi-select support: arrays of names + canonical
    // contact ids (index-aligned). The legacy `partner` / `partner_contact_id`
    // fields above stay in sync as first-entry mirrors so any legacy
    // consumer reads the primary partner cleanly.
    partner_names: [],
    partner_contact_ids: [],
    // Iter 85.38 · Optional Activity association on ORDINARY meeting
    // notes so a planning call / prep session / debrief can roll up
    // under an Activity Briefing without becoming a second activity
    // itself. Only surfaced when the composer knows a Client +
    // Engagement (backend enforces the same scope match).
    activity_briefing_id: "",
  });

  const load = () => {
    api.get("/notes").then((r) => setNotes(r.data));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/engagements").then((r) => setEngs(r.data));
  };
  useEffect(() => { load(); }, []);

  // ── Deep-link · `?open=<note_id>` ──
  // Iter 80 · Signals-from-the-field rows (Notes & briefings, Decisions)
  // and Suggested engagement "Briefing ↗" links navigate to
  // /notes?open=<id>. On mount, if the param is present, fetch that
  // note and open the canonical NoteViewerDialog (NOT the Briefing
  // Review dialog — that's a one-time extraction workflow, opening a
  // reviewed note should present it as a normal working Note). Param
  // is stripped after consumption so a reload doesn't re-open the dialog.
  const openParamConsumedRef = useRef(false);
  useEffect(() => {
    if (openParamConsumedRef.current) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    openParamConsumedRef.current = true;
    const seen = notes.find((n) => n.id === openId);
    if (seen) {
      setViewerNote(seen);
    } else {
      api.get(`/notes/${openId}`).then((r) => setViewerNote(r.data)).catch(() => {});
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, notes, setSearchParams]);

  // Dynamic-aware + engagement-aware seeding: when the advisor lands via
  // /notes?dynamic=X and/or /notes?client=…&engagement=…, open the create
  // dialog pre-seeded with the relevant tag, prompt guidance, and inherited
  // client/engagement context. Query params are stripped after consumption
  // so reloading the page doesn't re-open the dialog.
  useEffect(() => {
    const q = searchParams.get("dynamic");
    const clientParam = searchParams.get("client");
    const engagementParam = searchParams.get("engagement");
    const key = q ? DYNAMIC_QUERY_TO_KEY[q] : null;
    const dynamic = key ? CONTINUOUS_DYNAMICS[key] : null;
    const hasInherited = !!(clientParam || engagementParam);

    if (!dynamic && !hasInherited) return;

    if (hasInherited) {
      setInheritedContext({
        clientId: clientParam || null,
        engagementId: engagementParam || null,
      });
      setContextLocked(true);
    }

    setForm((f) => {
      const base = {
        ...f,
        client_id: clientParam || f.client_id,
        engagement_id: engagementParam || f.engagement_id,
      };
      if (dynamic) {
        return {
          ...base,
          title: `${dynamic.shortLabel} — reflection ${new Date().toLocaleDateString()}`,
          briefing_kind: "meeting",
          note_type: "meeting",
          tags: DYNAMIC_TAG[key],
          body: `Working notes · ${dynamic.shortLabel}\n\n${dynamic.workingNotesPrompt}\n\n— — —\n`,
        };
      }
      return base;
    });
    if (dynamic) setDynamicIntent(dynamic);
    setOpen(true);
    // Strip consumed query params so subsequent navigation (or refresh)
    // doesn't relaunch the dialog. `replace: true` keeps history clean.
    const next = new URLSearchParams(searchParams);
    next.delete("dynamic");
    next.delete("client");
    next.delete("engagement");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter 85.41 · Pre-Scoped Follow-up: /notes?followup=<activityId>
  // opens the composer already scoped to the activity's Client +
  // Engagement, with briefing_kind='followup' and the activity link
  // pre-filled. Lands the partner one keystroke away from writing a
  // post-event debrief tied to the correct activity.
  const followupParamRef = useRef(false);
  useEffect(() => {
    const activityId = searchParams.get("followup");
    if (!activityId) return;
    if (followupParamRef.current) return;
    followupParamRef.current = true;
    // Fetch the activity to inherit its scope, then open the composer.
    api.get(`/notes/${activityId}`).then((r) => {
      const activity = r.data;
      if (!activity || activity.note_type !== "activity_briefing") return;
      resetForm();
      setForm((f) => ({
        ...f,
        title: `Follow-up · ${activity.title}`,
        note_type: "meeting",
        briefing_kind: "followup",
        client_id: activity.client_id || "",
        engagement_id: activity.engagement_id || "",
        activity_briefing_id: activity.id,
      }));
      setOpen(true);
    }).catch(() => {});
    const next = new URLSearchParams(searchParams);
    next.delete("followup");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resetForm = () => setForm({
    title: "", body: "", note_type: "meeting", client_id: "", engagement_id: "",
    tags: "", attendees: [], briefing_kind: "meeting",
    new_client_name: "", new_engagement_name: "",
    is_new_client: false, is_new_engagement: false,
    activity_date: "", activity_location: "", activity_service_category: "",
    activity_briefing_id: "",
  });

    const startBriefing = (kind) => {
      const label = {
        meeting: `Meeting — ${new Date().toLocaleDateString()}`,
        client_briefing: "Partner org briefing",
        engagement_briefing: "Engagement briefing",
        activity_briefing: "Activity briefing",
        followup: "Follow-up briefing",
      }[kind] || "New briefing";
      resetForm();
      setForm((f) => ({
        ...f,
        title: label,
        // 'followup' is a distinct briefing kind on the backend but stays a
        // note_type='meeting' record (so it renders + records like any meeting).
        briefing_kind: kind === "followup" ? "followup" : kind,
        note_type: BRIEFING_TYPES.has(kind) ? kind : "meeting",
        // Default an activity to today so the composer surfaces a sensible
        // date pill immediately; user can still change it.
        activity_date: kind === "activity_briefing" ? new Date().toISOString().slice(0, 10) : "",
      }));
      setOpen(true);
    };

  const submit = async () => {
    // Body is optional across every note type — a partner may just want to
    // press Record and let YadidAI transcribe/organize afterwards. Title is
    // auto-filled from note_type + today's date when left blank so the record
    // can always be created without friction (Iter 90 — Refinement 6).
    const isBriefing = BRIEFING_TYPES.has(form.note_type);
    const fallbackTitle = () => {
      const stamp = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
      const kind = {
        meeting: "Untitled Meeting",
        insight: "Untitled Insight",
        strategic_memo: "Untitled Memo",
        call_log: "Untitled Call Log",
        diagnostic_memo: "Untitled Memo",
        client_briefing: "Untitled Partner Org Briefing",
        engagement_briefing: "Untitled Engagement Briefing",
        activity_briefing: "Untitled Activity Briefing",
      }[form.note_type] || "Untitled Note";
      return `${kind} — ${stamp}`;
    };
    const finalTitle = form.title.trim() || fallbackTitle();

    // Resolve target for briefings
    let clientId = form.client_id || null;
    let engagementId = form.engagement_id || null;

    try {
      const { data: created } = await api.post("/notes", {
        title: finalTitle,
        body: form.body || (isBriefing ? "(Briefing draft — record or paste material)" : "(Recorded meeting — awaiting transcription)"),
        note_type: form.note_type,
        client_id: clientId,
        engagement_id: engagementId,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        attendees: Array.isArray(form.attendees)
          ? form.attendees.map((s) => (s || "").trim()).filter(Boolean)
          : String(form.attendees || "").split(",").map((s) => s.trim()).filter(Boolean),
        // Iter 85.34 · Activity Briefing metadata — persisted only when
        // note_type == "activity_briefing"; blank strings map to null so
        // the DB stays clean on other briefing types.
        activity_date: form.note_type === "activity_briefing" ? (form.activity_date || null) : null,
        activity_location: form.note_type === "activity_briefing" ? (form.activity_location.trim() || null) : null,
        activity_service_category: form.note_type === "activity_briefing" ? (form.activity_service_category || null) : null,
        // Iter 85.38 · Meeting notes can carry an optional Activity
        // association so partner tasks/decisions spawned from the note
        // preserve the Client → Engagement → Activity → Meeting Note →
        // Task lineage. Activity briefings themselves never self-
        // associate, and non-meeting briefings don't offer the picker,
        // so send null in those cases so the DB stays clean.
        activity_briefing_id: form.note_type === "meeting" ? (form.activity_briefing_id || null) : null,
        // Iter 85.67 · Contextual Partner (individual/collaborator, NOT
        // the primary Partner Organization). Trimmed empty → null so the
        // note document stays clean.
        partner: form.partner?.trim() || null,
        // Iter 85.76 · Multiple contextual Partners. `partner_contact_ids`
        // is the canonical list; we mirror `partner_contact_id` to the
        // first entry for legacy consumers. `contact_ids` is a de-duped
        // union so the per-Contact history drawer surfaces the note
        // under every linked person.
        partner_contact_id: form.partner_contact_id || (form.partner_contact_ids?.[0] || null),
        partner_contact_ids: form.partner_contact_ids || (form.partner_contact_id ? [form.partner_contact_id] : []),
        contact_ids: (() => {
          const legacy = form.contact_ids || [];
          const multi = form.partner_contact_ids || [];
          const single = form.partner_contact_id ? [form.partner_contact_id] : [];
          return Array.from(new Set([...legacy, ...multi, ...single].filter(Boolean)));
        })(),
      });

      // For briefings that participate in the extract/apply proposal
      // pipeline (client_briefing, engagement_briefing, and follow-up
      // meeting notes), set the briefing target so `/briefing/extract`
      // knows which canonical record proposals should mutate. Activity
      // briefings do NOT participate in that pipeline — they carry
      // their own activity_date/location/service_category metadata
      // and are read/organized like meeting notes — so we skip the
      // target PUT for them. Sending activity_briefing to
      // `/briefing/target` would 422 (its BriefingKind Literal does
      // not include activity_briefing) and the resulting Pydantic
      // detail array would crash the UI when passed to toast.
      const usesBriefingTarget =
        form.note_type === "client_briefing" ||
        form.note_type === "engagement_briefing" ||
        form.briefing_kind === "followup";
      if (usesBriefingTarget) {
        await api.put(`/notes/${created.id}/briefing/target`, {
          target: {
            kind: form.briefing_kind,
            client_id: clientId,
            engagement_id: engagementId,
            new_client_name: form.is_new_client ? form.new_client_name.trim() || null : null,
            new_engagement_name: form.is_new_engagement ? form.new_engagement_name.trim() || null : null,
          },
        });
      }

      resetForm();
      setOpen(false);
      toast.success(isBriefing ? "Briefing draft created" : "Note saved");
      load();
    } catch (e) {
      // FastAPI 422 responses ship `detail` as an array of Pydantic
      // error entries (`{type, loc, msg, input, ctx, url}`). Passing
      // that array/object directly into toast.error would crash React
      // ("Objects are not valid as a React child"). Normalize any
      // shape (string / list of entries / single entry / unknown) to
      // a human-readable string before rendering.
      toast.error(formatApiError(e, "Failed to save"));
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this entry?")) return;
    await api.delete(`/notes/${id}`);
    load();
  };

  const filtered = useMemo(() => notes.filter((n) => {
    if (typeFilter !== "all" && n.note_type !== typeFilter) return false;
    if (clientFilter !== "all" && n.client_id !== clientFilter) return false;
    if (stateFilter === "open" && n.briefing_state === "archived") return false;
    if (stateFilter !== "all" && stateFilter !== "open" && n.briefing_state !== stateFilter) return false;
    return true;
  }), [notes, typeFilter, clientFilter, stateFilter]);

  const clientName = (cid) => clients.find(c => c.id === cid)?.name;
  const engName = (eid) => engs.find(e => e.id === eid)?.name;
  // Fast lookup for the Seeded-from breadcrumb (chain visualization). Building
  // this once outside the render loop avoids an O(n²) walk when the feed grows.
  const noteTitleById = useMemo(() => {
    const m = new Map();
    for (const nn of notes) m.set(nn.id, nn.title);
    return m;
  }, [notes]);

  const TILES = [
    // Iter 85.71 · Card order updated per product direction:
    // Meeting → Activity → Partner → Engagement → Follow-up.
    // Meeting leads because it's the highest-frequency capture surface
    // and reads left-to-right into the working chain of records that
    // usually follow (Activity/Partner/Engagement) or the reflective
    // Follow-up that closes a thread.
    //
    // Iter 85.94 · Copy anchored to the user's canonical mental model:
    //  · Meeting     — the recurring partner conversation; default when
    //                  nothing bigger is starting. Where most discovery
    //                  happens.
    //  · Activity    — a discrete event *inside* an existing engagement
    //                  (e.g. a convening you're organizing). NOT a
    //                  standalone activity, NOT the engagement itself.
    //  · Partner     — the meeting spins up a NEW client profile.
    //  · Engagement  — client already exists; kicks off a NEW contract.
    //  · Follow-up   — a follow-up to an existing meeting or thread;
    //                  just adds notes onto the prior record.
    { kind: "meeting",             label: "Meeting",     blurb: "Recurring partner conversation — where most of your discoveries happen. Default when nothing bigger is starting.", icon: Microphone },
    { kind: "activity_briefing",   label: "Activity",    blurb: "A discrete event inside an existing engagement (e.g. a convening you're organizing).", icon: CalendarStar },
    { kind: "client_briefing",     label: "Partner",     blurb: "The conversation will spin up a new client profile.",                          icon: Users },
    { kind: "engagement_briefing", label: "Engagement",  blurb: "Client already exists — kicks off a new engagement (new contract).", icon: Briefcase },
    { kind: "followup",            label: "Follow-up",   blurb: "A follow-up to an existing meeting or activity — just adds notes onto the prior record.", icon: ArrowUUpLeft },
  ];
  const heroTile = TILES[0];
  const secTiles = TILES.slice(1);

  return (
    <PageShell size="standard">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-brass" />
            Advisory Desk
          </h1>
        </div>
        {/* Iter 85.98.1 · Single "View" dropdown menu (Pattern D).
            Replaces the paired segmented toggles with one polished
            entry-point à la Notion / Linear. Groups Layout (List /
            Grid / Kanban) and List density (Comfortable / Compact)
            in one menu; density items are visibly present but
            disabled when Layout ≠ List so nothing ever appears or
            disappears from the toolbar. */}
        {(() => {
          const layoutOpts = [
            { key: "list",   label: "List",   icon: Rows,         hint: "Vertical rows with previews" },
            { key: "grid",   label: "Grid",   icon: SquaresFour,  hint: "3-column header-only tiles" },
            { key: "kanban", label: "Kanban", icon: Kanban,       hint: "Columns grouped by note type" },
          ];
          const densityOpts = [
            { key: "comfortable", label: "Comfort", hint: "Full note previews" },
            { key: "compact",     label: "Compact", hint: "Header-only rows" },
          ];
          const activeLayout = layoutOpts.find((o) => o.key === deskView) || layoutOpts[0];
          const ActiveIcon = activeLayout.icon;
          const summary = deskView === "list"
            ? `${activeLayout.label} · ${isDeskCompact ? "Compact" : "Comfort"}`
            : activeLayout.label;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="desk-view-menu-trigger"
                  aria-label={`View settings — currently ${summary}`}
                  className="h-10 px-4 border border-border text-muted-foreground hover:text-primary hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors inline-flex items-center gap-2 text-[0.625rem] font-mono uppercase tracking-widest"
                >
                  <ActiveIcon size={14} weight="bold" />
                  <span>View · {summary}</span>
                  <CaretDown size={10} weight="bold" className="opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none min-w-[260px]" data-testid="desk-view-menu">
                <DropdownMenuLabel className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                  Layout
                </DropdownMenuLabel>
                {layoutOpts.map((o) => {
                  const Icon = o.icon;
                  const active = deskView === o.key;
                  return (
                    <DropdownMenuItem
                      key={o.key}
                      data-testid={`desk-view-${o.key}`}
                      onSelect={(e) => { e.preventDefault(); setDeskView(o.key); }}
                      className="py-2.5 gap-3 cursor-pointer justify-between"
                      aria-checked={active}
                    >
                      <span className="inline-flex items-center gap-3">
                        <Icon size={14} weight={active ? "fill" : "bold"} className={active ? "text-brass" : "text-muted-foreground"} />
                        <span className={`text-sm ${active ? "text-brass font-medium" : ""}`}>{o.label}</span>
                      </span>
                      {active && <Check size={12} weight="bold" className="text-brass" />}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                  List density
                </DropdownMenuLabel>
                {densityOpts.map((o) => {
                  const active = (o.key === "compact") === isDeskCompact;
                  const disabled = deskView !== "list";
                  return (
                    <DropdownMenuItem
                      key={o.key}
                      data-testid={`desk-density-${o.key}`}
                      disabled={disabled}
                      onSelect={(e) => { e.preventDefault(); if (!disabled) setDensity(o.key); }}
                      className="py-2 gap-3 cursor-pointer justify-between pl-9"
                      aria-checked={active}
                      title={disabled ? "Density applies to List only" : o.hint}
                    >
                      <span className={`text-sm ${active && !disabled ? "text-brass font-medium" : ""}`}>{o.label}</span>
                      {active && !disabled && <Check size={12} weight="bold" className="text-brass" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })()}
      </div>

      {/*
        Iter 85.94.1 · Start-a-briefing tiles — Direction D hero layout.
        ─────────────────────────────────────────────────────────────
        Two-column split at lg+:
          · Left (2/5)  — Meeting hero, tall, brass CTA.
          · Right (3/5) — 2×2 grid of Activity / Partner / Engagement /
                          Follow-up so each secondary sits at ~30% of
                          viewport (fixes the previous 1/6-column
                          overflow where "Follow-up" hyphenated and
                          descriptions bled downward).
        Tablet (md)  — hero full width, secondaries as a 2×2 below.
        Mobile       — everything stacks single-column.
      */}
      <div className="flex flex-col lg:flex-row gap-3 mb-8 items-stretch" data-testid="briefing-tiles">
        {/* ── Hero: Meeting ─────────────────────────────────────── */}
        {(() => {
          const { kind, label, blurb, icon: Icon } = heroTile;
          return (
            <button
              key={kind}
              data-testid={`briefing-tile-${kind}`}
              onClick={() => startBriefing(kind)}
              className="relative overflow-hidden group border border-border lg:w-2/5 min-h-[220px] p-6 text-left flex flex-col justify-between hover:border-brass hover:shadow-[0_12px_36px_-16px_rgba(200,167,91,0.4)] transition-all bg-gradient-to-br from-secondary/40 via-card to-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/60"
              style={{ backgroundImage: "linear-gradient(135deg, rgba(200,167,91,0.06) 0%, transparent 60%)" }}
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "radial-gradient(circle, rgba(200,167,91,0.16), transparent 70%)" }}
              />
              <div className="relative">
                <div className="inline-flex items-center justify-center h-12 w-12 border border-brass text-brass mb-5">
                  <Icon size={22} weight="fill" />
                </div>
                <h2 className="font-display text-3xl font-semibold tracking-tight leading-tight mb-2" style={{ hyphens: "none" }}>{label}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-md">{blurb}</p>
              </div>
              <div className="relative mt-5">
                <span className="inline-flex items-center gap-2 bg-brass text-[#1a1508] px-4 py-2 text-[0.6875rem] font-mono uppercase tracking-widest font-semibold group-hover:bg-brass/90 transition-colors">
                  Start capture <span aria-hidden="true">→</span>
                </span>
              </div>
            </button>
          );
        })()}

        {/* ── Secondary tiles: Activity / Partner / Engagement / Follow-up
              rendered as a 2×2 grid inside the right 3/5 column so each
              tile has real estate to breathe.
              Iter 85.99 · Match the Meeting hero's ambient brass glow
              but at reduced intensity so secondaries feel like the
              hero's kin without competing. Each tile gets a soft
              radial brass wash that fades from 0→100% opacity on
              hover, plus a matching shadow lift. */}
        <div className="lg:w-3/5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {secTiles.map(({ kind, label, blurb, icon: Icon }) => (
            <button
              key={kind}
              data-testid={`briefing-tile-${kind}`}
              onClick={() => startBriefing(kind)}
              className="relative overflow-hidden border border-border bg-card p-5 min-h-[100px] text-left hover:border-brass hover:shadow-[0_8px_24px_-14px_rgba(200,167,91,0.5)] hover:-translate-y-[1px] transition-all group flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/60"
            >
              {/* Ambient brass glow — smaller and softer than the hero's
                  so secondaries never out-shout Meeting. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "radial-gradient(circle, rgba(200,167,91,0.14), transparent 70%)" }}
              />
              <div className="relative inline-flex items-center justify-center h-9 w-9 border border-brass text-brass mb-3 group-hover:bg-brass-soft/40 transition-colors">
                <Icon size={16} weight="fill" />
              </div>
              <h3 className="relative font-display text-xl font-semibold tracking-tight leading-tight mb-1.5 whitespace-nowrap group-hover:text-brass transition-colors" style={{ hyphens: "none" }}>{label}</h3>
              <p className="relative text-xs text-muted-foreground leading-relaxed">{blurb}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          resetForm();
          setDynamicIntent(null);
          setInheritedContext(null);
          setContextLocked(false);
        }
      }}>
        <DialogContent className="rounded-none bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form.note_type === "activity_briefing"
                ? "New activity briefing"
                : (BRIEFING_TYPES.has(form.note_type)
                    ? (form.note_type === "client_briefing" ? "New partner org briefing" : "New engagement briefing")
                    : "New note")}
            </DialogTitle>
          </DialogHeader>
          {dynamicIntent && (
            <div
              data-testid="briefing-dynamic-banner"
              className="border border-brass/50 bg-brass-soft/30 px-3 py-2 text-[0.6875rem] font-mono uppercase tracking-widest text-brass flex items-center gap-2"
            >
              <Sparkle size={11} weight="fill" />
              Capturing under the {dynamicIntent.shortLabel} lens · this tag will link the briefing back to that dynamic
            </div>
          )}
          {inheritedContext && (
            <div
              data-testid="briefing-inherited-banner"
              className="border border-primary/50 bg-primary/5 px-3 py-2 flex items-center justify-between gap-3 flex-wrap"
            >
              <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-primary flex items-center gap-2">
                {contextLocked ? <LockKey size={12} weight="fill" /> : <PencilSimple size={12} weight="fill" />}
                <span>
                  Inherited from engagement ·{" "}
                  {inheritedContext.clientId && (
                    <span className="text-foreground normal-case tracking-normal font-sans">
                      {clients.find(c => c.id === inheritedContext.clientId)?.name || "client"}
                    </span>
                  )}
                  {inheritedContext.clientId && inheritedContext.engagementId && " · "}
                  {inheritedContext.engagementId && (
                    <span className="text-foreground normal-case tracking-normal font-sans">
                      {engs.find(e => e.id === inheritedContext.engagementId)?.name || "engagement"}
                    </span>
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setContextLocked(!contextLocked)}
                data-testid="briefing-change-context"
                className="text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:text-primary border border-brass/60 hover:border-primary px-2 py-1 transition-colors"
              >
                {contextLocked ? "Change context" : "Re-lock context"}
              </button>
            </div>
          )}
          <Input data-testid="note-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none bg-secondary/40" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Briefing type</label>
              <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v, briefing_kind: BRIEFING_TYPES.has(v) ? v : "meeting" })}>
                <SelectTrigger data-testid="note-type-select" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_TYPES.map(t => <SelectItem key={t} value={t}>{NOTE_TYPE_LABEL[t] || t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Client selector — supports "new prospective client" for briefings.
                Locked (disabled) when inherited context is present and the
                advisor hasn't clicked "Change context". */}
            <div>
              <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Partner Organization</label>
              {form.is_new_client ? (
                <Input
                  data-testid="new-client-name"
                  placeholder="New partner organization name"
                  value={form.new_client_name}
                  onChange={(e) => setForm({ ...form, new_client_name: e.target.value })}
                  className="rounded-none bg-secondary/40"
                />
              ) : (
                <Select
                  value={form.client_id || "none"}
                  disabled={contextLocked && !!inheritedContext?.clientId}
                  onValueChange={(v) => {
                    if (v === "__new__") setForm({ ...form, is_new_client: true, client_id: "" });
                    // Changing the client wipes the engagement selection so
                    // we never end up with an engagement that doesn't belong
                    // to the currently-selected client. Iter 85.38 · also
                    // clears any Activity association since activities are
                    // scoped to (client, engagement).
                    else setForm({ ...form, client_id: v === "none" ? "" : v, engagement_id: "", activity_briefing_id: "" });
                  }}
                >
                  <SelectTrigger data-testid="client-select" className="rounded-none bg-secondary/40"><SelectValue placeholder="Partner organization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(None)</SelectItem>
                    {BRIEFING_TYPES.has(form.note_type) && <SelectItem value="__new__">+ New prospective partner org</SelectItem>}
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {form.is_new_client && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_new_client: false, new_client_name: "" })}
                  className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary mt-1"
                >
                  ← Pick existing
                </button>
              )}
            </div>
            {/* Engagement selector — filtered by the currently-selected
                client so advisors can't accidentally attach a briefing to an
                engagement outside that client's book. */}
            <div>
              <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Engagement</label>
              {form.is_new_engagement ? (
                <Input
                  data-testid="new-engagement-name"
                  placeholder="New engagement name"
                  value={form.new_engagement_name}
                  onChange={(e) => setForm({ ...form, new_engagement_name: e.target.value })}
                  className="rounded-none bg-secondary/40"
                />
              ) : (
                <Select
                  value={form.engagement_id || "none"}
                  disabled={contextLocked && !!inheritedContext?.engagementId}
                  onValueChange={(v) => {
                    if (v === "__new__") setForm({ ...form, is_new_engagement: true, engagement_id: "" });
                    // Iter 85.38 · switching engagement resets any
                    // Activity association since activities live under
                    // a specific engagement.
                    else setForm({ ...form, engagement_id: v === "none" ? "" : v, activity_briefing_id: "" });
                  }}
                >
                  <SelectTrigger data-testid="engagement-select" className="rounded-none bg-secondary/40"><SelectValue placeholder="Engagement" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(None)</SelectItem>
                    {form.note_type === "engagement_briefing" && <SelectItem value="__new__">+ New engagement</SelectItem>}
                    {engs
                      .filter(en => !form.client_id || en.client_id === form.client_id)
                      .map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {form.is_new_engagement && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_new_engagement: false, new_engagement_name: "" })}
                  className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary mt-1"
                >
                  ← Pick existing
                </button>
              )}
            </div>
          </div>

          {!BRIEFING_TYPES.has(form.note_type) && (
            <AttendeeSelector
              value={Array.isArray(form.attendees) ? form.attendees : String(form.attendees || "").split(",").map((s) => s.trim()).filter(Boolean)}
              onChange={(next) => setForm({ ...form, attendees: next })}
              clientId={form.client_id || null}
              engagementId={form.engagement_id || null}
              testIdPrefix="composer-attendees"
            />
          )}
          {/* Iter 85.67 · Contextual Partner selector. Iter 85.68 ·
              Relationship-aware autocomplete over the currently-selected
              Partner Org's canonical stakeholders — one-click links to a
              real Contact record (so the memo threads into that Contact's
              history drawer), free-text OK, prevents duplicate Contact
              creation when the typed name already exists. */}
          <div>
            <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Partner <span className="text-muted-foreground/60 lowercase tracking-normal font-sans">· contextual, optional</span>
            </label>
            <PartnerContactsMultiAutocomplete
              value={{
                partner_names: form.partner_names || (form.partner ? [form.partner] : []),
                partner_contact_ids: form.partner_contact_ids || (form.partner_contact_id ? [form.partner_contact_id] : []),
              }}
              onChange={({ partner_names, partner_contact_ids, partner, partner_contact_id }) => setForm({
                ...form,
                partner_names,
                partner_contact_ids,
                // Iter 85.76 · Keep the legacy single-value mirrors up
                // to date so existing consumers (memo renderers, feed
                // cards) still read the joined name / primary id.
                partner,
                partner_contact_id,
              })}
              clientId={form.client_id || null}
              testIdPrefix="composer-partner"
            />
          </div>
          {/*
            Iter 85.38 · Optional Activity association on ordinary
            Meeting Notes. The picker is ALWAYS shown for meeting
            notes (regardless of whether activities exist yet) so
            partners can see the mechanism and the "no activities yet"
            hint prompts them to create an Activity briefing first.
            Client + Engagement remain required — the picker cannot
            scope activities without both.
          */}
          {form.note_type === "meeting" && (() => {
            const canScope = !!form.client_id && !!form.engagement_id;
            const scopedActivities = canScope ? notes.filter((nn) =>
              nn.note_type === "activity_briefing"
              && nn.client_id === form.client_id
              && nn.engagement_id === form.engagement_id
            ) : [];
            return (
              <div data-testid="meeting-activity-picker">
                <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  Activity <span className="text-muted-foreground/60 normal-case tracking-normal">(optional — link to a partner activity)</span>
                </label>
                {!canScope ? (
                  <div className="text-xs text-muted-foreground italic border border-dashed border-border px-3 py-2">
                    Pick a Partner Organization and Engagement above to see linkable activities.
                  </div>
                ) : scopedActivities.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic border border-dashed border-border px-3 py-2">
                    No activities under this Client / Engagement yet. Create an Activity briefing to link one.
                  </div>
                ) : (
                  <Select
                    value={form.activity_briefing_id || "none"}
                    onValueChange={(v) => setForm({ ...form, activity_briefing_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="activity-select" className="rounded-none bg-secondary/40"><SelectValue placeholder="No activity" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No activity — standalone meeting note</SelectItem>
                      {scopedActivities.map(a => {
                        const when = a.activity_date ? ` · ${a.activity_date}` : "";
                        return <SelectItem key={a.id} value={a.id}>{a.title}{when}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })()}
          {/* Iter 85.34 · Activity Briefing metadata row (date · location ·
              service class). Rendered inline in the composer so an
              advisor sets the discrete activity's shape at capture time —
              same canonical fields also editable later via the Note
              Viewer's activity strip. */}
          {form.note_type === "activity_briefing" && (
            <div className="border border-brass/40 bg-brass-soft/10 p-3 space-y-3" data-testid="activity-metadata">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-brass">Activity details</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Date</label>
                  <Input
                    data-testid="activity-date"
                    type="date"
                    value={form.activity_date}
                    onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
                    className="rounded-none bg-secondary/40"
                  />
                </div>
                <div>
                  <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Location</label>
                  <Input
                    data-testid="activity-location"
                    placeholder="Venue, city, or virtual"
                    value={form.activity_location}
                    onChange={(e) => setForm({ ...form, activity_location: e.target.value })}
                    className="rounded-none bg-secondary/40"
                  />
                </div>
                <div>
                  <label className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Service category</label>
                  <Select
                    value={form.activity_service_category || "none"}
                    onValueChange={(v) => setForm({ ...form, activity_service_category: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="activity-service-category" className="rounded-none bg-secondary/40"><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">(None)</SelectItem>
                      {ACTIVITY_SERVICE_CATEGORIES.map(sc => (
                        <SelectItem key={sc.value} value={sc.value}>{sc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-none bg-secondary/40" />
          <Textarea
            data-testid="note-body"
            placeholder={BRIEFING_TYPES.has(form.note_type)
              ? "Optional — you can also record audio after creating the draft."
              : "Optional — paste an agenda, prior notes, or leave blank and record the meeting."}
            rows={BRIEFING_TYPES.has(form.note_type) ? 5 : 8}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="rounded-none bg-secondary/40"
          />
          {BRIEFING_TYPES.has(form.note_type) && (
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground border-l-2 border-brass/60 pl-3">
              After you save, open the briefing to record audio or extract proposals with YadidAI.
              <span className="text-brass"> Nothing writes to the canonical client/engagement/playbook until you accept a proposal.</span>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="note-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground">
              {BRIEFING_TYPES.has(form.note_type) ? "Save briefing draft" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MeetingRecallSearch onOpenNote={(nid) => {
        const el = document.querySelector(`[data-testid="note-${nid}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }} />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="notes-type-filter" className="rounded-none bg-secondary/40 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {ALL_TYPES.map(t => <SelectItem key={t} value={t}>{NOTE_TYPE_LABEL[t] || t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger data-testid="notes-client-filter" className="rounded-none bg-secondary/40 w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All partners</SelectItem>
            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger data-testid="notes-state-filter" className="rounded-none bg-secondary/40 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open (hide archived)</SelectItem>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {(typeFilter !== "all" || clientFilter !== "all" || stateFilter !== "open") && (
          <button
            onClick={() => { setTypeFilter("all"); setClientFilter("all"); setStateFilter("open"); }}
            data-testid="notes-clear-filters"
            className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary px-3"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Iter 85.98 · View router. When advisor picks Grid or Kanban
          from the top-right toggle we short-circuit the vertical list
          into a `NoteTile` grid. Each tile is header-only for maximum
          scannability and wraps in a HoverCard so hovering a tile
          reveals a content-preview snippet with attendees, decisions,
          and the first ~240 chars of body — maximizes reflection
          before committing to the full Open action.
      */}
      {(() => {
        if (deskView === "list") return null; // fall through to legacy list

        // Shared preview builder — pulls the strongest available snippet
        // in order: organized summary → memo body → raw body → title.
        const previewSnippet = (n) => {
          const ms = n.meeting_structured || {};
          if (ms.summary) return String(ms.summary).slice(0, 280);
          if (n.body && n.body.trim()) {
            // Drop markdown scaffolding for a cleaner tooltip read.
            const stripped = String(n.body)
              .replace(/^#+\s*/gm, "")
              .replace(/\*\*/g, "")
              .replace(/^-+\s*/gm, "• ")
              .replace(/\n{3,}/g, "\n\n")
              .trim();
            return stripped.slice(0, 280);
          }
          return "(No content yet)";
        };
        const isMeetingBriefTile = (n) =>
          MARKDOWN_NOTE_TYPES.has(n.note_type) &&
          (n.tags || []).includes("meeting-brief");

        // NoteTile — a compact card with hover-preview. Used by both
        // Grid and Kanban render paths. Click opens the full viewer.
        const NoteTile = ({ n }) => {
          const isMemo = isMeetingBriefTile(n);
          const partner = clientName(n.client_id);
          const engagement = engName(n.engagement_id);
          const ms = n.meeting_structured || {};
          const decCount = (ms.decisions || []).length;
          const actCount = (ms.action_items || []).length;
          const when = n.meeting_date || n.created_at;
          const whenLabel = when ? new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
          const typeLabel = isMemo ? "Strategic memo" : (NOTE_TYPE_LABEL[n.note_type] || n.note_type.replace(/_/g, " "));
          return (
            <HoverCard openDelay={220} closeDelay={80}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  onClick={() => setViewerNote(n)}
                  data-testid={`note-tile-${n.id}`}
                  className={`relative w-full text-left p-4 border transition-colors flex flex-col gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
                    isMemo
                      ? "border-brass/50 bg-gradient-to-br from-brass-soft/12 via-card to-card hover:border-brass"
                      : "border-border bg-card hover:border-primary/60"
                  }`}
                >
                  {isMemo && (
                    <span aria-hidden="true" className="pointer-events-none absolute top-0 bottom-0 left-0 w-1 bg-brass" />
                  )}
                  <div className={`text-[0.625rem] font-mono uppercase tracking-widest ${isMemo ? "text-brass" : "text-primary"}`}>
                    {typeLabel}
                  </div>
                  <div className="font-display text-sm font-semibold leading-tight line-clamp-2 text-foreground">
                    {n.title || "Untitled"}
                  </div>
                  <div className="text-[0.6875rem] text-muted-foreground line-clamp-1">
                    {[partner, engagement].filter(Boolean).join(" · ")}
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-border/40 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                    <span>{whenLabel}</span>
                    <span className="flex items-center gap-2">
                      {decCount > 0 && <span>{decCount} dec</span>}
                      {actCount > 0 && <span>{actCount} act</span>}
                    </span>
                  </div>
                </button>
              </HoverCardTrigger>
              <HoverCardContent
                side="top"
                align="start"
                className="w-96 rounded-none bg-popover border-brass/40 text-sm p-4"
                data-testid={`note-tile-preview-${n.id}`}
              >
                <div className={`text-[0.625rem] font-mono uppercase tracking-widest mb-1 ${isMemo ? "text-brass" : "text-primary"}`}>
                  {typeLabel}
                </div>
                <div className="font-display text-base font-semibold leading-tight mb-2">
                  {n.title || "Untitled"}
                </div>
                <div className="text-xs text-muted-foreground mb-3">
                  {[partner, engagement, whenLabel].filter(Boolean).join(" · ")}
                </div>
                {(n.attendees || []).length > 0 && (
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    Attendees · <span className="text-foreground normal-case tracking-normal font-sans">{n.attendees.join(", ")}</span>
                  </div>
                )}
                <div className="text-xs leading-relaxed text-foreground/80 whitespace-pre-wrap max-h-40 overflow-hidden">
                  {previewSnippet(n)}
                </div>
                {(decCount > 0 || actCount > 0) && (
                  <div className="mt-3 pt-2 border-t border-border/40 flex items-center gap-3 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                    {decCount > 0 && <span>{decCount} decisions</span>}
                    {actCount > 0 && <span>{actCount} action items</span>}
                  </div>
                )}
              </HoverCardContent>
            </HoverCard>
          );
        };

        if (deskView === "grid") {
          if (filtered.length === 0) {
            return <div className="border border-border bg-card p-12 text-center text-xs text-muted-foreground" data-testid="desk-view-empty">No entries yet.</div>;
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="desk-grid-view">
              {filtered.map((n) => <NoteTile key={n.id} n={n} />)}
            </div>
          );
        }

        // Kanban — 4 typed lanes. Any non-canonical note types (insight,
        // diagnostic_memo, etc.) land in the closest bucket: memos into
        // Strategic memos, everything else into Meetings.
        const lanes = [
          { key: "meeting", label: "Meetings", brass: false, match: (n) => n.note_type === "meeting" || (!isMeetingBriefTile(n) && n.note_type !== "activity_briefing" && n.note_type !== "followup") },
          { key: "activity", label: "Activity briefings", brass: false, match: (n) => n.note_type === "activity_briefing" },
          { key: "memo", label: "Strategic memos", brass: true, match: (n) => isMeetingBriefTile(n) },
          { key: "followup", label: "Follow-ups", brass: false, match: (n) => n.note_type === "followup" },
        ];
        const lanesFilled = lanes.map((l) => ({ ...l, notes: filtered.filter(l.match) }));
        // De-dupe: a note may match Meetings-catch-all and Strategic memo.
        // Priority: memo → activity → followup → meeting.
        const seen = new Set();
        const priority = ["memo", "activity", "followup", "meeting"];
        const byKey = Object.fromEntries(lanesFilled.map((l) => [l.key, l]));
        priority.forEach((k) => {
          byKey[k].notes = byKey[k].notes.filter((n) => {
            if (seen.has(n.id)) return false;
            seen.add(n.id);
            return true;
          });
        });
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="desk-kanban-view">
            {lanes.map((l) => {
              const lane = byKey[l.key];
              return (
                <div key={l.key} className="bg-secondary/20 border border-border p-3 min-h-[320px]" data-testid={`desk-kanban-lane-${l.key}`}>
                  <div className="flex items-center justify-between pb-2 mb-3 border-b border-border/60">
                    <span className={`text-[0.625rem] font-mono uppercase tracking-widest ${l.brass ? "text-brass" : "text-primary"}`}>
                      {l.label}
                    </span>
                    <span className="text-[0.625rem] font-mono text-muted-foreground bg-border/40 px-2 py-0.5">
                      {lane.notes.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {lane.notes.length === 0 && (
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/60 italic p-3">
                        Empty
                      </div>
                    )}
                    {lane.notes.map((n) => <NoteTile key={n.id} n={n} />)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {deskView === "list" && (
      <div className="space-y-3">
        {filtered.length === 0 && <div className="border border-border bg-card p-12 text-center text-xs text-muted-foreground">No entries yet.</div>}
        {filtered.map((n) => {
          const isBriefing = BRIEFING_TYPES.has(n.note_type);
          const state = n.briefing_state;
          const openProposals = (n.briefing_proposals?.items || []).filter(i => i.status === "proposed").length;
          const appliedProposals = (n.briefing_proposals?.items || []).filter(i => i.status === "applied").length;
          // Iter 85.43 · Advisory Desk collapsed card = compact WORKING
          // surface (not passive summary). Header + bounded scrollable
          // note preview + type-appropriate action toolbar are rendered
          // inline; Open expands into the full record for reading. The
          // toolbar re-mounts the canonical MeetingNoteCard in
          // `variant="compact"` mode so every button (Organize, Promote,
          // Post-brief, Send-digest, language chips) invokes the exact
          // same endpoint the full viewer uses — zero functional
          // duplication.
          const ms = n.meeting_structured || {};
          const decisions = (ms.decisions || []).length;
          const actions = (ms.action_items || []).length;
          const isDraftBody = n.body === "(Briefing draft — record or paste material)" || n.body === "(Recorded meeting)";
          // The compact preview prefers the organized summary when
          // available so the card reads like an executive tl;dr; if
          // the note isn't organized yet the raw body is shown inside
          // a bounded scroller so partners can skim without dumping
          // the entire long transcript into the feed.
          const hasContent = !isDraftBody && !!(n.body || "").trim();
          const showsWorkingCard = n.note_type === "meeting" || n.note_type === "activity_briefing";
          // Iter 85.49 · Meeting Brief detection hoisted so both the
          // preview IIFE (memo document render) and the after-tags
          // block (export footer) can gate on it consistently.
          const isMeetingBrief =
            MARKDOWN_NOTE_TYPES.has(n.note_type) &&
            (n.tags || []).includes("meeting-brief");
          return (
            <div
              key={n.id}
              className={`relative border transition-colors ${
                isMeetingBrief
                  ? "border-brass/50 bg-gradient-to-br from-brass-soft/12 via-card to-card hover:border-brass"
                  : "border-border bg-card hover:border-primary/60"
              }`}
              data-testid={`note-${n.id}`}
            >
              {/* Iter 85.97 · Distinct brass rail on the left of strategic
                  memo (meeting-brief) cards so advisors identify them
                  at a glance next to raw meetings, activity briefings,
                  and other note types. The rail + soft brass wash
                  reinforce the "AI-authored, exportable document" nature
                  of these records without shouting. */}
              {isMeetingBrief && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0 bottom-0 left-0 w-1 bg-brass"
                />
              )}
              <div className="p-5 flex items-start justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setViewerNote(n)}
                  className="flex-1 min-w-0 text-left focus:outline-none focus-visible:outline-none"
                  data-testid={`note-card-body-${n.id}`}
                >
                  <div className={`text-[0.625rem] font-mono uppercase tracking-widest flex items-center gap-2 flex-wrap ${isMeetingBrief ? "text-brass" : "text-primary"}`}>
                    <span>{isMeetingBrief ? "Strategic memo" : (NOTE_TYPE_LABEL[n.note_type] || n.note_type.replace(/_/g, " "))}</span>
                    <AttachmentBadge note={n} />
                    <ActivityPill activityId={n.activity_briefing_id} activityTitleBy={noteTitleById} testidSuffix={n.id} />
                    {state && (
                      <span className={`border px-1.5 py-0.5 ${STATE_PILL[state] || ""}`} data-testid={`note-state-${n.id}`}>
                        {STATE_LABEL[state] || state}
                      </span>
                    )}
                    {openProposals > 0 && <span className="text-brass">{openProposals} in review</span>}
                    {appliedProposals > 0 && <span className="text-emerald-500">{appliedProposals} applied</span>}
                  </div>
                  <div className="font-display font-bold text-base mt-0.5 truncate" title={n.title}>{n.title}</div>
                  {n.source_briefing_note_id && noteTitleById.has(n.source_briefing_note_id) && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = document.querySelector(`[data-testid="note-${n.source_briefing_note_id}"]`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); const el = document.querySelector(`[data-testid="note-${n.source_briefing_note_id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); } }}
                      data-testid={`chain-source-${n.id}`}
                      className="text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:text-primary inline-flex items-center gap-1 mt-1 cursor-pointer"
                    >
                      Seeded from → {noteTitleById.get(n.source_briefing_note_id)}
                    </span>
                  )}
                  <div className="mt-1 text-[0.6875rem] text-muted-foreground flex flex-wrap gap-x-3">
                    {n.client_id && <span>Partner: {clientName(n.client_id) || "—"}</span>}
                    {n.engagement_id && <span>Engagement: {engName(n.engagement_id) || "—"}</span>}
                    {n.author_name && <span>By {n.author_name}</span>}
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                    {(decisions > 0 || actions > 0) && <span className="text-primary">{decisions} dec · {actions} act</span>}
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    data-testid={`open-note-viewer-${n.id}`}
                    onClick={() => setViewerNote(n)}
                    size="sm"
                    variant="outline"
                    className="rounded-none h-8 border-primary/60 text-primary hover:bg-primary/10"
                  >
                    Open
                  </Button>
                  <button onClick={() => del(n.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-note-${n.id}`}><Trash size={14} /></button>
                </div>
              </div>
              {/*
                Bounded scrollable preview window.
                · Before Organize: raw body (or previewText for markdown
                  note types) rendered in a compact scroller so long
                  transcripts don't blow out the feed.
                · After Organize (`meeting_structured` populated with
                  any section): reuse the shared `OrganizedNoteBody`
                  used inside the Open reading view — full Summary,
                  Key Points, Decisions, Action Items, Open Questions,
                  Follow-ups, Suggested Engagements in canonical
                  section order — inside the SAME bounded scroller so
                  the card stays compact while the entire organized
                  record is reachable via internal scroll.
                Container label "ORGANIZED NOTE" wraps the scroller;
                `OrganizedNoteBody` renders its own SUMMARY / KEY
                POINTS / etc. headings inside.
              */}
              {(() => {
                // Iter 85.85 · Card density — compact hides the entire
                // note-body preview (organized + raw) so the desk reads
                // as a scannable list of headers + toolbar rows.
                if (isDeskCompact) return null;
                const ms2 = n.meeting_structured || {};
                const hasOrganized = !!(
                  ms2.summary ||
                  ms2.decisions?.length ||
                  ms2.action_items?.length ||
                  ms2.key_points?.length ||
                  ms2.follow_ups?.length ||
                  ms2.open_questions?.length ||
                  ms2.scope_summary ||
                  ms2.objectives?.length ||
                  ms2.key_risks?.length ||
                  ms2.deliverables?.length ||
                  ms2.strategic_context ||
                  ms2.known_priorities?.length ||
                  ms2.known_pain_points?.length
                );
                // Iter 85.49 · Meeting Brief detection hoisted to the
                // parent scope (see `isMeetingBrief` above) so the
                // export footer can render AFTER the tags/attendees
                // row instead of before it (Iter 85.50 reorder).
                if (isMeetingBrief && (n.body || "").trim()) {
                  const client = n.client_id ? clients.find((c) => c.id === n.client_id) : null;
                  const eng = n.engagement_id ? engs.find((e) => e.id === n.engagement_id) : null;
                  return (
                    <div className="px-5 pb-4">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-brass mb-1">Meeting brief</div>
                      <div
                        className="border border-border/60 bg-secondary/30 px-3 py-2 max-h-80 overflow-y-auto"
                        data-testid={`note-preview-${n.id}`}
                      >
                        <MarkdownDocument variant="memo">
                          {toMemoMarkdown(n.body, n, client, eng)}
                        </MarkdownDocument>
                      </div>
                    </div>
                  );
                }
                if (!hasContent && !hasOrganized) {
                  return (
                    <div className="px-5 pb-4">
                      <p className="text-xs italic text-brass/80" data-testid={`note-preview-${n.id}`}>
                        <Waveform size={11} weight="fill" className="inline mr-1" />
                        No material captured yet. Open to record audio or paste text.
                      </p>
                    </div>
                  );
                }
                if (hasOrganized) {
                  return (
                    <div className="px-5 pb-4">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-brass mb-1">Organized note</div>
                      <div
                        className="border border-border/60 bg-secondary/30 px-3 py-2 max-h-80 overflow-y-auto"
                        data-testid={`note-preview-${n.id}`}
                      >
                        <OrganizedNoteBody
                          note={n}
                          onNoteChange={() => load()}
                        />
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="px-5 pb-4">
                    <div
                      className="border border-border/60 bg-secondary/30 px-3 py-2 max-h-40 overflow-y-auto"
                      data-testid={`note-preview-${n.id}`}
                    >
                      <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                        {MARKDOWN_NOTE_TYPES.has(n.note_type) ? previewText(n.body) : n.body}
                      </p>
                    </div>
                  </div>
                );
              })()}
              {(n.tags?.length > 0 || n.attendees?.length > 0) && (
                <div className="px-5 pb-3 flex gap-1.5 flex-wrap">
                  {n.attendees?.slice(0, 4).map((a, i) => <span key={"a" + i} className="text-[0.625rem] font-mono border border-border px-2 py-0.5 text-muted-foreground">@ {a}</span>)}
                  {n.attendees?.length > 4 && <span className="text-[0.625rem] font-mono text-muted-foreground">+{n.attendees.length - 4}</span>}
                  {n.tags?.slice(0, 5).map((t, i) => <span key={"t" + i} className="text-[0.625rem] font-mono uppercase tracking-widest border border-primary/40 text-primary px-2 py-0.5">{t}</span>)}
                </div>
              )}
              {/* Iter 85.50 · Meeting Brief export footer moved BELOW
                  the tags/attendees row so the visual reading order is:
                    1. participant / status indicators (@ pills,
                       MEETING-BRIEF, AUTO-POSTED)
                    2. export actions (Copy as Word · Download as PDF)
                  `MemoActions` is the SAME component the NoteViewerDialog
                  mounts, so both surfaces invoke the same yadidDoc
                  export pipeline (canonical navy #0F1E3D). Right-aligned
                  on a subtle tools-surface tint band. */}
              {isMeetingBrief && (n.body || "").trim() && (
                <div
                  className="border-t border-tools-surface bg-tools-surface px-5 py-2 flex justify-end"
                  data-testid={`note-brief-footer-${n.id}`}
                >
                  <MemoActions markdown={n.body} title={n.title || "Meeting brief"} />
                </div>
              )}
              {/*
                Iter 85.45 · Collapsible Tools drawer — shared pattern
                across all Advisory Desk record types. Default state is
                collapsed so the feed reads as a clean scanning list.
                Expanded contents differ by note type (existing
                eligibility logic preserved):
                  · meeting / activity_briefing / meeting-with-followup
                    → MeetingNoteCard compact (Organize, Promote, Post,
                      Send, Record, Upload, lang chips, Activity
                      picker, follow-up CTA, Related notes)
                  · client_briefing / engagement_briefing → the
                    "Review with YadidAI" briefing-apply entry point
                The drawer is not rendered for note types with no
                eligible tools. Expand/collapse state is session-scoped
                via `expandedTools` in the parent BriefingStudio state.
              */}
              {(() => {
                const isReviewType = n.note_type === "client_briefing" || n.note_type === "engagement_briefing";
                const hasTools = showsWorkingCard || isReviewType;
                if (!hasTools) return null;
                const isOpen = expandedTools.has(n.id);
                return (
                  <div
                    className="border-t border-tools-surface"
                    data-testid={`note-tools-${n.id}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleTools(n.id); }}
                      aria-expanded={isOpen}
                      aria-controls={`note-tools-panel-${n.id}`}
                      data-testid={`note-tools-toggle-${n.id}`}
                      className="w-full flex items-center justify-between px-5 py-2 bg-tools-surface text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary focus:outline-none focus-visible:outline-none"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Wrench size={11} weight="bold" /> Tools
                      </span>
                      {isOpen
                        ? <CaretUp size={12} weight="bold" aria-hidden="true" />
                        : <CaretDown size={12} weight="bold" aria-hidden="true" />}
                    </button>
                    {isOpen && (
                      <div
                        id={`note-tools-panel-${n.id}`}
                        className="bg-tools-surface px-5 py-3"
                        data-testid={`note-toolbar-${n.id}`}
                      >
                        {showsWorkingCard && (
                          <MeetingNoteCard note={n} onUpdated={load} variant="compact" />
                        )}
                        {isReviewType && (
                          <div className="flex flex-wrap items-center gap-3">
                            <Button
                              data-testid={`open-briefing-review-${n.id}`}
                              onClick={() => setReviewNote(n)}
                              size="sm"
                              variant="outline"
                              className="rounded-none border-brass/60 text-brass hover:bg-brass-soft"
                            >
                              <Sparkle size={11} weight="fill" className="mr-1.5" /> Review with YadidAI
                            </Button>
                            <span className="text-[0.6875rem] text-muted-foreground">
                              Extract proposals and apply them into the canonical {n.note_type === "client_briefing" ? "client" : "engagement"} record.
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
      )}

      <BriefingReviewDialog
        open={!!reviewNote}
        onClose={() => setReviewNote(null)}
        note={reviewNote}
        onChanged={load}
      />
      {/* Iter 80 · Canonical Note Viewer — opened via the per-card "Open"
          button and `?open=<id>` deep-links. Uses the shared
          OrganizedNoteBody so Action Items are fully interactive
          (complete, edit owner/due, promote to Task) regardless of
          whether the record went through AI review first. */}
      <NoteViewerDialog
        note={viewerNote}
        open={!!viewerNote}
        onOpenChange={(v) => { if (!v) setViewerNote(null); }}
        onNoteChange={(next) => { setViewerNote(next); load(); }}
      />
    </PageShell>
  );
}
